import React, { useCallback, useRef, useState } from 'react';
import { MAX_QUOTE_FILES } from '../data/quote';
import { isRequiredQuoteField, requiredQuoteFields, validateQuote, validateQuoteField } from '../utils/quoteValidation';
import type { QuoteErrors, QuoteFieldName, QuoteValues } from '../types/quote';

const initialValues: QuoteValues = {
  name: '',
  phone: '',
  email: '',
  company: '',
  service: '',
  vehicle: '',
  details: '',
  timing: '',
};

type FieldElement = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

export function useQuoteForm() {
  const [values, setValues] = useState<QuoteValues>(initialValues);
  const [errors, setErrors] = useState<QuoteErrors>({});
  const [files, setFiles] = useState<File[]>([]);
  const [fileNotice, setFileNotice] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const handleChange = useCallback((e: React.ChangeEvent<FieldElement>) => {
    const name = e.target.name as QuoteFieldName;
    const value = e.target.value;
    setValues((prev) => ({ ...prev, [name]: value }));
    if (isRequiredQuoteField(name)) {
      setErrors((prev) => (prev[name] ? { ...prev, [name]: validateQuoteField(name, value) } : prev));
    }
  }, []);

  const handleBlur = useCallback((e: React.FocusEvent<FieldElement>) => {
    const { name, value } = e.target;
    if (isRequiredQuoteField(name) && value.trim()) {
      setErrors((prev) => ({ ...prev, [name]: validateQuoteField(name, value) }));
    }
  }, []);

  const addFiles = useCallback(
    (list: FileList | null) => {
      if (!list || list.length === 0) return;
      const incoming = Array.from(list);
      const room = MAX_QUOTE_FILES - files.length;
      if (incoming.length > room) {
        setFileNotice(`You can attach up to ${MAX_QUOTE_FILES} files. Only the first ${Math.max(room, 0)} were added.`);
      } else {
        setFileNotice('');
      }
      setFiles([...files, ...incoming.slice(0, Math.max(room, 0))]);
    },
    [files],
  );

  const removeFile = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setFileNotice('');
  }, []);

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const next = validateQuote(values);
      setErrors(next);
      const firstInvalid = requiredQuoteFields.find((field) => next[field]);
      if (firstInvalid) {
        formRef.current?.querySelector<HTMLElement>(`[name="${firstInvalid}"]`)?.focus();
        return;
      }
      setSubmitted(true);
    },
    [values],
  );

  const reviewForm = useCallback(() => setSubmitted(false), []);

  return {
    values,
    errors,
    files,
    fileNotice,
    submitted,
    formRef,
    handleChange,
    handleBlur,
    addFiles,
    removeFile,
    handleSubmit,
    reviewForm,
  };
}

export type QuoteFormState = ReturnType<typeof useQuoteForm>;
